import { useState } from "react";
import { Badge, cn } from "web-design-system";
import { usePoorlyPerformingFunds } from "creso-core/hooks";
import {
  PoorlyPerformingFund,
  PoorlyPerformingReason,
  PortfolioReviewBlock,
  PortfolioReviewSource,
} from "creso-core/types";
import { convertToCurrency, formatAsPercentage } from "creso-core/utils";
import { AmcLogo } from "@core/components/logo/amc-logo";
import { ReviewBlockState, ReviewSection } from "./review-section";
import { ReviewSourceSelect } from "./review-source-select";

const REASON_LABEL: Record<PoorlyPerformingReason, string> = {
  LOW_QUARTILE: "Low 3Y quartile rank in category",
  BELOW_BENCHMARK: "Underperforming benchmark",
};

const ROMAN = ["i", "ii", "iii", "iv"];

export function PoorlyPerformingFunds({
  investmentAccountId,
  overviewBlock,
  isOverviewLoading,
}: {
  investmentAccountId: string;
  // The overview call covers the default "All holdings" view, so the
  // standalone endpoint is only hit when the filter moves.
  overviewBlock?: PortfolioReviewBlock<PoorlyPerformingFund[]>;
  isOverviewLoading: boolean;
}) {
  const [source, setSource] = useState<PortfolioReviewSource>("ALL");
  const useOverview = source === "ALL";

  const filtered = usePoorlyPerformingFunds(
    { investment_account_guid: investmentAccountId, source },
    { enabled: !useOverview }
  );

  const funds = useOverview ? overviewBlock?.data : filtered.data;
  const isLoading = useOverview ? isOverviewLoading : filtered.isLoading;
  const isError = useOverview ? overviewBlock?.ok === false : filtered.isError;

  return (
    <ReviewSection title="Poorly performing funds">
      <ReviewSourceSelect value={source} onChange={setSource} />
      <ReviewBlockState
        isLoading={isLoading}
        isError={isError}
        isEmpty={!!funds && !funds.length}
        emptyMessage="No poorly performing funds in these holdings."
      >
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-200 text-sm">
            <thead className="bg-card">
              <tr>
                <th className="w-90 p-4 text-left font-semibold">Fund Name</th>
                {[
                  "Source",
                  "Current",
                  "3Y Trailing",
                  "Sharpe",
                  "3Y Quartile",
                ].map((label) => (
                  <th key={label} className="p-4 text-right font-semibold">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            {funds?.map((fund) => (
              <PoorlyPerformingRow key={fund.scheme_isin} fund={fund} />
            ))}
          </table>
        </div>
      </ReviewBlockState>
    </ReviewSection>
  );
}

function PoorlyPerformingRow({ fund }: { fund: PoorlyPerformingFund }) {
  const alpha = fund.alpha_3y === null ? null : Number(fund.alpha_3y);
  return (
    <tbody className="border-t border-border">
      <tr className="[&>td]:px-4 [&>td]:py-3 [&>td]:text-right [&>td:first-child]:text-left">
        <td>
          <div className="flex items-center gap-3">
            <AmcLogo amc_code={fund.amc_morningstar_code} />
            <div className="flex min-w-0 flex-col gap-1">
              <span className="truncate">{fund.scheme_name}</span>
              <span className="truncate text-xs text-muted-foreground">
                {fund.category}
              </span>
            </div>
          </div>
        </td>
        <td>
          <Badge
            variant="custom"
            className="bg-tag-slate font-semibold text-tag-slate-foreground"
          >
            {fund.source_label}
          </Badge>
        </td>
        <td>
          <div className="flex flex-col items-end gap-1">
            <span>{convertToCurrency(fund.current_value)}</span>
            <span className="text-xs text-muted-foreground">
              {formatAsPercentage(fund.pct_of_aum, "-", 0)} of AUM
            </span>
          </div>
        </td>
        <td>
          <div className="flex flex-col items-end gap-1">
            <span>{formatAsPercentage(fund.return_3y, "-")}</span>
            {alpha !== null ? (
              <Badge
                variant="custom"
                className={cn(
                  "px-1 text-xxs font-normal",
                  alpha < 0
                    ? "bg-tag-red text-tag-red-foreground"
                    : "bg-tag-green text-tag-green-foreground"
                )}
              >
                {alpha > 0 ? "+" : ""}
                {formatAsPercentage(alpha, "-", 1)} alpha
              </Badge>
            ) : null}
          </div>
        </td>
        <td>
          {fund.sharpe_3y === null ? "-" : Number(fund.sharpe_3y).toFixed(2)}
        </td>
        <td>
          {fund.quartile ? (
            <Badge
              variant="custom"
              className={cn(
                "font-semibold",
                fund.quartile >= 3
                  ? "bg-tag-red text-tag-red-foreground"
                  : "bg-tag-slate text-tag-slate-foreground"
              )}
            >
              Q{fund.quartile}
            </Badge>
          ) : (
            "-"
          )}
        </td>
      </tr>
      <tr>
        <td colSpan={6} className="px-4 pb-3">
          <div className="ml-auto w-full max-w-142 rounded-lg bg-tag-slate px-4 py-3 text-xs text-tag-slate-foreground">
            {fund.reasons
              .map((r, i) => `${ROMAN[i]}) ${REASON_LABEL[r]}`)
              .join("; ")}
          </div>
        </td>
      </tr>
    </tbody>
  );
}
